<div class="analytics-container">

  @if (IsInitializing) {
    <div class="loading-overlay"><div class="spinner"></div> Loading...</div>
  }

  @if (!IsInitializing && !IsAuthorizedUser) {
    <div class="empty-state">
      <i class="fa-solid fa-lock"></i>
      <p>No organization context available. Please log in with an organization account.</p>
    </div>
  }

  @if (!IsInitializing && IsAuthorizedUser) {

    <!-- Page Header -->
    <div class="page-header">
      <div class="page-title-area">
        <div class="page-icon"><i class="fa-solid fa-chart-bar"></i></div>
        <div>
          <h1 class="page-title">{{ CurrentOrgName || 'Organization' }} Analytics</h1>
          <p class="page-subtitle">Activity, components, and cost metrics</p>
        </div>
      </div>
      <div class="page-actions">
        <div class="month-nav">
          <button class="btn-nav" (click)="PrevMonth()" title="Previous month">
            <i class="fa-solid fa-chevron-left"></i>
          </button>
          <select class="month-select" [value]="SelectedMonth" (change)="OnMonthSelect($event)">
            @for (m of MonthNames; track $index) {
              <option [value]="$index" [disabled]="IsMonthDisabled($index)" [selected]="$index === SelectedMonth">{{ m }}</option>
            }
          </select>
          <select class="year-select" [value]="SelectedYear" (change)="OnYearSelect($event)">
            @for (y of AvailableYears; track y) {
              <option [value]="y" [selected]="y === SelectedYear">{{ y }}</option>
            }
          </select>
          <button class="btn-nav" (click)="NextMonth()" [disabled]="IsCurrentMonth" title="Next month">
            <i class="fa-solid fa-chevron-right"></i>
          </button>
          @if (!IsCurrentMonth) {
            <button class="btn-today" (click)="GoToCurrentMonth()">Today</button>
          }
        </div>
        <button class="btn-action" (click)="LoadAllData()">
          <i class="fa-solid fa-arrows-rotate"></i> Refresh
        </button>
        <button class="btn-action" (click)="ExportCSV()">
          <i class="fa-solid fa-download"></i> Export CSV
        </button>
      </div>
    </div>

    <!-- Error Banner -->
    @if (Error) {
      <div class="error-banner">
        <i class="fa-solid fa-circle-exclamation"></i>
        {{ Error }}
        <button (click)="Error = null">&times;</button>
      </div>
    }

    <!-- Sub-Tab Bar -->
    <div class="tab-bar">
      <div class="tab-group">
        <button class="tab-btn" [class.active]="ActiveTab === 'activity'" (click)="SwitchTab('activity')">
          <i class="fa-solid fa-chart-line"></i> Activity
        </button>
        <button class="tab-btn" [class.active]="ActiveTab === 'components'" (click)="SwitchTab('components')">
          <i class="fa-solid fa-puzzle-piece"></i> Components
        </button>
        <button class="tab-btn" [class.active]="ActiveTab === 'costs'" (click)="SwitchTab('costs')">
          <i class="fa-solid fa-dollar-sign"></i> Costs
        </button>
      </div>
    </div>

    <!-- ==================== ACTIVITY TAB ==================== -->
    @if (ActiveTab === 'activity') {

      @if (ConversationView === 'list') {

        <!-- Daily Activity Chart -->
        <div class="section-card">
          <div class="section-header">
            <h3 class="section-title"><i class="fa-solid fa-chart-column"></i> Daily Activity</h3>
          </div>
          @if (ActivityLoading) {
            <div class="loading-overlay compact"><div class="spinner"></div></div>
          } @else if (FilledDailyActivity.length > 0) {
            <div class="chart-container">
              <div class="chart-with-axis">
                <div class="y-axis">
                  @for (tick of ActivityYAxisTicks; track $index) {
                    <span class="y-tick">{{ FormatAxisCount(tick) }}</span>
                  }
                </div>
                <div class="chart-area">
                  @for (tick of ActivityYAxisTicks; track $index) {
                    @if ($index > 0) {
                      <div class="gridline" [style.bottom.%]="GetGridlinePosition(tick, NiceMaxActivity)"></div>
                    }
                  }
                  <div class="bar-chart">
                    @for (row of FilledDailyActivity; track row.day) {
                      <div class="bar-col" [class.bar-col-zero]="row.conversationCount === 0"
                           [title]="FormatShortDate(row.day) + ': ' + row.conversationCount + ' conversations'">
                        @if (row.conversationCount > 0) {
                          <span class="bar-value">{{ row.conversationCount }}</span>
                        }
                        <div class="bar bar-activity" [style.height.%]="GetActivityBarHeight(row.conversationCount)"></div>
                        <span class="bar-label">{{ GetShortDay(row.day) }}</span>
                      </div>
                    }
                  </div>
                </div>
              </div>
            </div>
          } @else {
            <div class="empty-state-sm">
              <i class="fa-solid fa-chart-column"></i>
              <p>No daily activity data for this month</p>
            </div>
          }
        </div>

        <!-- Per-Person Breakdown -->
        <div class="section-card">
          <div class="section-header">
            <h3 class="section-title"><i class="fa-solid fa-users"></i> Activity by Contact</h3>
            @if (SelectedPersonID) {
              <button class="btn-sm" (click)="ClearPersonFilter()">
                <i class="fa-solid fa-filter-circle-xmark"></i> Clear Filter
              </button>
            }
          </div>
          @if (ActivityLoading) {
            <div class="loading-overlay compact"><div class="spinner"></div></div>
          } @else if (Persons.length > 0) {
            <div class="data-table">
              <div class="table-header">
                <span class="col-name">Contact</span>
                <span class="col-num">Conversations</span>
              </div>
              @for (person of Persons; track person.personID) {
                <div class="table-row table-row-clickable" [class.table-row-selected]="SelectedPersonID === person.personID"
                     (click)="FilterByPerson(person.personID)">
                  <span class="col-name">
                    {{ person.personName }}
                    @if (SelectedPersonID === person.personID) {
                      <i class="fa-solid fa-filter filter-active-icon"></i>
                    }
                  </span>
                  <span class="col-num">{{ person.conversationCount }}</span>
                </div>
              }
            </div>
          } @else {
            <div class="empty-state-sm">
              <i class="fa-solid fa-users"></i>
              <p>No contact activity data for this month</p>
            </div>
          }
        </div>

        <!-- Conversations List -->
        <div class="section-card">
          <div class="section-header">
            <h3 class="section-title">
              <i class="fa-solid fa-comments"></i> Conversations
              @if (SelectedPersonID) {
                <span class="filter-badge">Filtered</span>
              }
            </h3>
            <span class="section-badge">{{ Conversations.length }} total</span>
          </div>
          @if (ConversationsLoading) {
            <div class="loading-overlay compact"><div class="spinner"></div></div>
          } @else if (Conversations.length > 0) {
            <div class="data-table">
              <div class="table-header">
                <span class="col-name">Name</span>
                <span class="col-contact">Contact</span>
                <span class="col-num">Exchanges</span>
                <span class="col-status">Failures</span>
                <span class="col-date">Created</span>
              </div>
              @for (conv of Conversations; track conv.conversationID) {
                <div class="table-row table-row-clickable" (click)="SelectConversation(conv)">
                  <span class="col-name">{{ conv.name || 'Conversation' }}</span>
                  <span class="col-contact">{{ conv.personName }}</span>
                  <span class="col-num">{{ conv.exchangeCount }}</span>
                  <span class="col-status">
                    @if (conv.hasFailures) {
                      <span class="failure-dot" title="Has failures"></span>
                    }
                  </span>
                  <span class="col-date">{{ FormatDate(conv.createdAt) }}</span>
                </div>
              }
            </div>
          } @else {
            <div class="empty-state-sm">
              <i class="fa-solid fa-comments"></i>
              <p>No conversations found</p>
            </div>
          }
        </div>

      }

      <!-- ── Conversation Detail View ── -->
      @if (ConversationView === 'detail' && SelectedConversation) {

        <!-- Back button + conversation header -->
        <div class="detail-header">
          <button class="btn-back" (click)="BackToConversationList()">
            <i class="fa-solid fa-arrow-left"></i> Back to Conversations
          </button>
          <div class="detail-title">
            <h2>{{ SelectedConversation.name || 'Conversation' }}</h2>
          </div>
        </div>

        <!-- Conversation Metadata -->
        <div class="detail-summary">
          <div class="summary-item">
            <span class="summary-label">Contact</span>
            <span class="summary-value">{{ SelectedConversation.personName }}</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">Created</span>
            <span class="summary-value">{{ FormatDate(SelectedConversation.createdAt) }}</span>
          </div>
          <div class="summary-item">
            <span class="summary-label">Exchanges</span>
            <span class="summary-value">{{ SelectedConversation.exchangeCount }}</span>
          </div>
          @if (SelectedConversation.hasFailures) {
            <div class="summary-item">
              <span class="summary-label">Status</span>
              <span class="summary-value status-failed-text">Has Failures</span>
            </div>
          }
        </div>

        <!-- Exchanges (Chat Bubbles) -->
        @if (ExchangesLoading) {
          <div class="loading-overlay"><div class="spinner"></div> Loading conversation...</div>
        } @else {
          <div class="exchange-list">
            @for (ex of Exchanges; track ex.id) {

              <!-- User Message -->
              @if (ex.role === 'User') {
                <div class="chat-row user-row">
                  <div class="chat-bubble user-bubble">
                    <div class="bubble-header">
                      <i class="fa-solid fa-user"></i> <span>User</span>
                      <span class="bubble-time">{{ FormatDate(ex.createdAt) }}</span>
                    </div>
                    @if (ex.message && IsFormMessage(ex.message)) {
                      <div class="bubble-body form-body">
                        <div class="form-header"><i class="fa-solid fa-list-check"></i> Form Response</div>
                        @for (field of ParseFormFields(ex.message); track field.label) {
                          <div class="form-field">
                            <span class="form-label">{{ field.label }}</span>
                            <span class="form-value">{{ field.displayValue }}</span>
                          </div>
                        }
                      </div>
                    } @else {
                      <div class="bubble-body">{{ ex.message || '\u2014' }}</div>
                    }
                  </div>
                </div>
              }

              <!-- AI Message -->
              @if (ex.role === 'AI') {
                <div class="chat-row ai-row">
                  <div class="chat-bubble ai-bubble">
                    <div class="bubble-header">
                      <i class="fa-solid fa-robot"></i> <span>Skip</span>
                      <span class="bubble-time">{{ FormatDate(ex.createdAt) }}</span>
                      @if (ex.agentRunStatus) {
                        <span class="status-badge sm" [ngClass]="GetStatusClass(ex.agentRunStatus)">{{ ex.agentRunStatus }}</span>
                      }
                      @if (ex.agentRunDurationMs) {
                        <span class="step-duration" [ngClass]="GetDurationClass(ex.agentRunDurationMs)">{{ FormatDuration(ex.agentRunDurationMs) }}</span>
                      }
                    </div>
                    @if (ex.message && IsFormMessage(ex.message)) {
                      <div class="bubble-body form-body">
                        <div class="form-header"><i class="fa-solid fa-list-check"></i> Form Response</div>
                        @for (field of ParseFormFields(ex.message); track field.label) {
                          <div class="form-field">
                            <span class="form-label">{{ field.label }}</span>
                            <span class="form-value">{{ field.displayValue }}</span>
                          </div>
                        }
                      </div>
                    } @else {
                      <div class="bubble-body">{{ ex.message || '\u2014' }}</div>
                    }

                    <!-- Error display -->
                    @if (ex.agentRunError) {
                      <div class="exchange-error">
                        <i class="fa-solid fa-circle-exclamation"></i>
                        <pre>{{ ex.agentRunError }}</pre>
                      </div>
                    }
                  </div>
                </div>
              }
            }

            @if (Exchanges.length === 0) {
              <div class="empty-state-sm">
                <i class="fa-solid fa-comments"></i>
                <p>No exchanges found in this conversation</p>
              </div>
            }
          </div>
        }
      }
    }

    <!-- ==================== COMPONENTS TAB ==================== -->
    @if (ActiveTab === 'components') {
      <div class="section-card">
        <div class="section-header">
          <h3 class="section-title"><i class="fa-solid fa-puzzle-piece"></i> Components</h3>
          <span class="section-badge">{{ FilteredComponents.length }} of {{ Components.length }}</span>
        </div>

        <!-- Search & Filter Bar -->
        <div class="comp-filter-bar">
          <div class="comp-search-wrap">
            <i class="fa-solid fa-magnifying-glass comp-search-icon"></i>
            <input type="text" class="comp-search-input" [(ngModel)]="ComponentSearch"
                   placeholder="Search components..."
                   (input)="ApplyComponentFilters()"
                   (keydown)="$event.stopPropagation()"
                   (keypress)="$event.stopPropagation()"
                   (keyup)="$event.stopPropagation()" />
          </div>
          @if (ComponentTypes.length > 1) {
            <select class="comp-type-select" [(ngModel)]="ComponentTypeFilter" (change)="ApplyComponentFilters()">
              <option value="">All Types</option>
              @for (t of ComponentTypes; track t) {
                <option [value]="t">{{ t }}</option>
              }
            </select>
          }
        </div>

        @if (ComponentsLoading) {
          <div class="loading-overlay compact"><div class="spinner"></div></div>
        } @else if (FilteredComponents.length > 0) {
          <div class="data-table">
            <div class="table-header">
              <span class="col-name sortable" (click)="SetComponentSort('componentName')">
                Component <i class="fa-solid" [ngClass]="GetComponentSortIcon('componentName')"></i>
              </span>
              <span class="col-type">Type</span>
              <span class="col-num sortable" (click)="SetComponentSort('versionCount')">
                Versions <i class="fa-solid" [ngClass]="GetComponentSortIcon('versionCount')"></i>
              </span>
              <span class="col-rating sortable" (click)="SetComponentSort('avgRating')">
                Rating <i class="fa-solid" [ngClass]="GetComponentSortIcon('avgRating')"></i>
              </span>
              <span class="col-num sortable" (click)="SetComponentSort('totalViews')">
                Views <i class="fa-solid" [ngClass]="GetComponentSortIcon('totalViews')"></i>
              </span>
              <span class="col-date sortable" (click)="SetComponentSort('lastGenerated')">
                Last Generated <i class="fa-solid" [ngClass]="GetComponentSortIcon('lastGenerated')"></i>
              </span>
            </div>
            @for (comp of FilteredComponents; track comp.componentID) {
              <div class="table-row">
                <span class="col-name">
                  <span class="comp-name">{{ comp.componentName }}</span>
                  @if (comp.title) {
                    <span class="comp-title">{{ comp.title }}</span>
                  }
                </span>
                <span class="col-type">
                  @if (comp.type) {
                    <span class="type-badge">{{ comp.type }}</span>
                  } @else {
                    <span class="text-muted">&mdash;</span>
                  }
                </span>
                <span class="col-num">{{ comp.versionCount }}</span>
                <span class="col-rating">
                  @if (comp.avgRating !== null) {
                    <span class="star-rating">
                      @for (filled of GetStarArray(comp.avgRating); track $index) {
                        @if (filled) {
                          <i class="fa-solid fa-star star-filled"></i>
                        } @else {
                          <i class="fa-regular fa-star star-empty"></i>
                        }
                      }
                    </span>
                    <span class="rating-number">{{ comp.avgRating.toFixed(1) }}</span>
                  } @else {
                    <span class="text-muted">&mdash;</span>
                  }
                </span>
                <span class="col-num">{{ comp.totalViews }}</span>
                <span class="col-date">{{ FormatDate(comp.lastGenerated) }}</span>
              </div>
            }
          </div>
        } @else if (Components.length > 0) {
          <div class="empty-state-sm">
            <i class="fa-solid fa-magnifying-glass"></i>
            <p>No components match your search</p>
          </div>
        } @else {
          <div class="empty-state-sm">
            <i class="fa-solid fa-puzzle-piece"></i>
            <p>No components found for this month</p>
          </div>
        }
      </div>
    }

    <!-- ==================== COSTS TAB ==================== -->
    @if (ActiveTab === 'costs') {

      @if (CostLoading && PerformanceLoading) {
        <div class="loading-overlay"><div class="spinner"></div> Loading cost data...</div>
      } @else {

        <!-- KPI Cards -->
        <div class="stats-cards">
          <div class="stat-card">
            <span class="stat-value cost-value">{{ FormatCost(MonthlyTotalCost) }}</span>
            <span class="stat-label">Total Cost</span>
          </div>
          <div class="stat-card">
            <span class="stat-value">{{ FormatTokens(MonthlyTotalTokens) }}</span>
            <span class="stat-label">Total Tokens</span>
          </div>
          <div class="stat-card">
            <span class="stat-value">{{ PerformanceData ? PerformanceData.componentsProduced : 0 }}</span>
            <span class="stat-label">Components Produced</span>
          </div>
          <div class="stat-card">
            <span class="stat-value perf-value">{{ PerformanceData ? FormatDurationMinSec(PerformanceData.avgProductionTimeMs) : '\u2014' }}</span>
            <span class="stat-label">Avg Production Time</span>
          </div>
        </div>

        <!-- Two-column grid: Daily Cost + Daily Usage -->
        <div class="metrics-grid">

          <!-- Daily Cost Chart -->
          <div class="section-card">
            <div class="section-header">
              <h3 class="section-title"><i class="fa-solid fa-chart-column"></i> Daily Cost</h3>
            </div>
            @if (CostLoading) {
              <div class="loading-overlay compact"><div class="spinner"></div></div>
            } @else if (FilledDailyCosts.length > 0) {
              <div class="chart-container">
                <div class="chart-with-axis">
                  <div class="y-axis">
                    @for (tick of CostYAxisTicks; track $index) {
                      <span class="y-tick">{{ FormatAxisCost(tick) }}</span>
                    }
                  </div>
                  <div class="chart-area">
                    @for (tick of CostYAxisTicks; track $index) {
                      @if ($index > 0) {
                        <div class="gridline" [style.bottom.%]="GetGridlinePosition(tick, NiceMaxCost)"></div>
                      }
                    }
                    <div class="bar-chart">
                      @for (row of FilledDailyCosts; track row.day) {
                        <div class="bar-col" [class.bar-col-zero]="row.totalCost === 0"
                             [title]="FormatShortDate(row.day) + ': ' + FormatCostPrecise(row.totalCost)">
                          @if (row.totalCost > 0) {
                            <span class="bar-value">{{ FormatCostPrecise(row.totalCost) }}</span>
                          }
                          <div class="bar" [style.height.%]="GetCostBarHeight(row.totalCost)"></div>
                          <span class="bar-label">{{ GetShortDay(row.day) }}</span>
                        </div>
                      }
                    </div>
                  </div>
                </div>
              </div>
            } @else {
              <div class="empty-state-sm">
                <i class="fa-solid fa-chart-column"></i>
                <p>No cost data for this month</p>
              </div>
            }
          </div>

          <!-- Daily Usage Chart -->
          <div class="section-card">
            <div class="section-header">
              <h3 class="section-title"><i class="fa-solid fa-comments"></i> Daily Conversations</h3>
            </div>
            @if (ActivityLoading) {
              <div class="loading-overlay compact"><div class="spinner"></div></div>
            } @else if (FilledDailyActivity.length > 0) {
              <div class="chart-container">
                <div class="chart-with-axis">
                  <div class="y-axis">
                    @for (tick of UsageYAxisTicks; track $index) {
                      <span class="y-tick">{{ FormatAxisCount(tick) }}</span>
                    }
                  </div>
                  <div class="chart-area">
                    @for (tick of UsageYAxisTicks; track $index) {
                      @if ($index > 0) {
                        <div class="gridline" [style.bottom.%]="GetGridlinePosition(tick, NiceMaxUsage)"></div>
                      }
                    }
                    <div class="bar-chart">
                      @for (row of FilledDailyActivity; track row.day) {
                        <div class="bar-col bar-col-usage" [class.bar-col-zero]="row.conversationCount === 0"
                             [title]="FormatShortDate(row.day) + ': ' + row.conversationCount + ' conversations'">
                          @if (row.conversationCount > 0) {
                            <span class="bar-value">{{ row.conversationCount }}</span>
                          }
                          <div class="bar bar-usage" [style.height.%]="GetUsageBarHeight(row.conversationCount)"></div>
                          <span class="bar-label">{{ GetShortDay(row.day) }}</span>
                        </div>
                      }
                    </div>
                  </div>
                </div>
              </div>
            } @else {
              <div class="empty-state-sm">
                <i class="fa-solid fa-comments"></i>
                <p>No conversation data for this month</p>
              </div>
            }
          </div>
        </div>

        <!-- Cost by Model -->
        <div class="section-card">
          <div class="section-header">
            <h3 class="section-title"><i class="fa-solid fa-microchip"></i> Cost by Model</h3>
          </div>
          @if (ModelCosts.length > 0) {
            <div class="model-breakdown">
              @for (row of ModelCosts; track row.modelName + row.vendorName) {
                <div class="model-row">
                  <div class="model-info">
                    <span class="model-name">{{ row.modelName }}</span>
                    <span class="model-vendor">{{ row.vendorName }}</span>
                  </div>
                  <div class="model-bar-wrap">
                    <div class="model-bar" [style.width.%]="GetModelBarPercent(row.totalCost)"></div>
                  </div>
                  <div class="model-stats">
                    <span class="model-cost">{{ FormatCostPrecise(row.totalCost) }}</span>
                    <span class="model-tokens">{{ FormatTokens(row.totalTokens) }} tokens</span>
                  </div>
                </div>
              }
            </div>
          } @else {
            <div class="empty-state-sm">
              <i class="fa-solid fa-microchip"></i>
              <p>No model cost data for this month</p>
            </div>
          }
        </div>

        <!-- Cost by Person -->
        <div class="section-card">
          <div class="section-header">
            <h3 class="section-title"><i class="fa-solid fa-users"></i> Cost by Person</h3>
          </div>
          @if (ContactCosts.length > 0) {
            <div class="model-breakdown">
              @for (row of ContactCosts; track row.personID) {
                <div class="model-row">
                  <div class="model-info">
                    <span class="model-name">{{ row.personName }}</span>
                    <span class="model-vendor">{{ row.runCount }} runs</span>
                  </div>
                  <div class="model-bar-wrap">
                    <div class="model-bar bar-person" [style.width.%]="GetContactCostBarPercent(row.totalCost)"></div>
                  </div>
                  <div class="model-stats">
                    <span class="model-cost">{{ FormatCostPrecise(row.totalCost) }}</span>
                    <span class="model-tokens">{{ FormatTokens(row.totalTokens) }} tokens</span>
                  </div>
                </div>
              }
            </div>
          } @else {
            <div class="empty-state-sm">
              <i class="fa-solid fa-users"></i>
              <p>No person cost data for this month</p>
            </div>
          }
        </div>
      }
    }
  }
</div>
